import {_} from 'lodash';
import * as ko from "knockout";
import {IMessageData, IObjectData, IRouteDefinition, IRouteDefinitionParams} from '../../index';
import {global} from "../../appContext";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {HIERARCHY_ARROW, MAX_RESULTS_PER_PAGE, ObjectTypeId, REQUEST_THROTTLE, WIN_NAME_IFRAME} from "../../constants";
import {createErrorMessage, getKnockoutThrottle, getObjectDefinition, getRawSyncFieldValue} from "../../util/misc";
import {getSearchObjectParams} from "../../util/request";
import {nsRoute} from "../../util/route";
import {REQUEST_MESSAGE} from "../../constants/Language";
import {ISplitButtonParams} from "@component/button/splitButton/split-button";
import {getHash, getRouteDropdownButton} from "../../constants/RouteDefinitions";
import {getSearchBoxHeadingKey} from "../search/boxHeadingKey";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import hierarchyHtml from './hierarchy.html?raw';

export let template = hierarchyHtml;

interface IDepartmentDetails {
    above: IDepartmentDetails | null
    below: IDepartmentDetails[]
    data: IObjectData
    departmentNumber: string
    id: number
    isLast: boolean
    managerName: string
    name: string
    parentDepartmentNumber: string
}

interface IParams {
    orgnr?: string
}

export class viewModel extends ParsedRouteDataDecorator {
    private readonly HIERARCHY_ARROW = HIERARCHY_ARROW
    private messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private current!: IDepartmentDetails;
    private path!: string[];
    private departments!: IDepartmentDetails[];
    private allTop!: IDepartmentDetails[];
    private isManagersReady: KnockoutObservable<boolean> = ko.observable(false);

    private routeDef!: IRouteDefinition;
    private isStandAlone: boolean = !!window.location.pathname.includes('win.html') || !global.login.isLoggedIn();

    private searchKey: KnockoutObservable<string> = ko.observable('').extend(getKnockoutThrottle({rateLimit: REQUEST_THROTTLE}))
    private isFiltered = ko.pureComputed(() => !!this.searchKey());
    private numDepartments: KnockoutObservable<number | null> = ko.observable(null);

    /** White-box title key (`*.search`); distinct from route `label` used for tabs / page H1. */
    private readonly searchBoxHeading = ko.pureComputed(() => getSearchBoxHeadingKey(this.__routeDef));

    /** Heading badge: full count when idle, hit count when searching. */
    private readonly searchBadge = ko.pureComputed((): number | null => {
        if (!this.isReady()) {
            return null;
        }
        return this.isFiltered() ? this.filtered().length : this.numDepartments();
    });

    /** Spinner on first load only (client-side filter is instant thereafter). */
    private readonly searchBadgeWaiting = ko.pureComputed((): boolean => !this.isReady());

    constructor(private params: IRouteDefinitionParams & any) {
        super(params);
        this.init();
    }

    private init() {
        this.isReady(false);
        this.isManagersReady(false);
        this.messageData({message: REQUEST_MESSAGE});
        const searchParams = getSearchObjectParams({
            objecttypeid: ObjectTypeId.Department,
            resultsprpage: MAX_RESULTS_PER_PAGE,
            sortcol: 'DepartmentNumber',
        });
        this.__requestRouteData?.request()
            .then((data: IObjectData[]) => {
                if (!data.length) {
                    this.messageData({message: `no data`, type: 'warning'});
                    return;
                }
                this.numDepartments(data.length);
                this.initDepartments(data);
                this.messageData(null);
                this.isReady(true);
                window.requestAnimationFrame(() => {
                    const routeId = this.__routeParams?.['id'] || global.getRouteId();
                    const el = document.getElementById('department' + routeId)
                    if (el) {
                        el.scrollIntoView({behavior: 'smooth', block: 'nearest'})
                    }
                })
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    private initDepartments(data: IObjectData[]) {
        let deps: IDepartmentDetails[] = [];
        const objDef = getObjectDefinition(ObjectTypeId.Department);
        if (!objDef) {
            return;
        }
        const nameKey = objDef.syncfieldDisplayName ?? [];

        const routeId = this.__routeParams?.['id'] || global.getRouteId();

        data.forEach((obj: IObjectData) => {
            const syncfields = obj.syncfields ?? [];
            // Use raw values so missing fields stay falsy (getSyncFieldValue returns "—").
            const departmentNumber = getRawSyncFieldValue('DepartmentNumber', syncfields) || '';
            const dep: IDepartmentDetails = {
                above: null, // populate later
                below: [], // populate later
                data: obj,
                departmentNumber,
                id: obj.id,
                isLast: false,
                managerName: obj.managername,
                name: getRawSyncFieldValue(nameKey, syncfields) || departmentNumber || '—',
                parentDepartmentNumber: getRawSyncFieldValue('ParentDepartmentNumber', syncfields) || '',
            };

            deps.push(dep);

            if (obj.id == routeId || obj.id == this.params.currentId) {
                this.current = dep;
            }
        });

        // populate above and below
        deps.forEach(dep => {
            if (!dep.parentDepartmentNumber || dep.parentDepartmentNumber == dep.departmentNumber) {
                return;
            }
            const parent = deps.find(_dep => _dep.departmentNumber == dep.parentDepartmentNumber);
            if (parent) {
                const isBelow = this.isBelow(dep.below, parent);
                if (!isBelow) {
                    dep.above = parent;
                    parent.below.push(dep);
                }
            }
        });

        // sort
        deps = this.sort(deps);
        deps.forEach(dep => dep.below = this.sort(dep.below));

        const allTop = deps.reduce((arr: IDepartmentDetails[], dep: IDepartmentDetails) => {
            const isTop = dep == this.getTop(dep);
            if (isTop) {
                arr.push(dep);
            }
            return arr;
        }, []);

        deps.forEach(dep => {
            const lastSibling = dep.above?.below[dep.above?.below.length - 1];
            dep.isLast = lastSibling == dep;
        });
        allTop[allTop.length - 1]!.isLast = true;

        this.path = this.current ? this.getPath(this.current) : [];

        this.departments = deps;
        this.allTop = allTop;
    }

    private sort(data: IDepartmentDetails[]): IDepartmentDetails[] {
        return _.sortBy(data, 'name');
    }

    private isBelow(below: IDepartmentDetails[], needle: IDepartmentDetails): boolean {
        return below.reduce((bool: boolean, dep: IDepartmentDetails) => {
            return bool || needle == dep || this.isBelow(dep.below, needle);
        }, false);
    }

    private getTop(department: IDepartmentDetails): IDepartmentDetails {
        const above = department.above;
        if (above) {
            return this.getTop(above);
        } else {
            return department;
        }
    }

    private getPath(department: IDepartmentDetails, arr: string[] = []): string[] {
        arr.unshift(department.departmentNumber);
        const above = department.above;
        if (above) {
            return this.getPath(above, arr);
        } else {
            return arr;
        }
    }

    private getDepartmentsToTop(department: IDepartmentDetails, arr: IDepartmentDetails[] = []): IDepartmentDetails[] {
        arr.unshift(department);
        const above = department.above;
        if (above) {
            return this.getDepartmentsToTop(above, arr);
        } else {
            return arr;
        }
    }

    private getRowCls(department: IDepartmentDetails): any {
        if (this.isPath(department)) {
            return 'node-active';
        }
    }

    private getNameCls(department: IDepartmentDetails): any {
        if (department == this.current) {
            return 'node-selected';
        }
    }

    private isPath(department: IDepartmentDetails): boolean {
        return this.path.indexOf(department.departmentNumber) > -1;
    }

    private filtered = ko.pureComputed(() => {
        const searchKey = this.searchKey();
        const include = this.departments?.filter(o => o.name?.toLowerCase().includes(searchKey?.toLowerCase())) ?? [];
        const ret = include.map(o => this.getDepartmentsToTop(o));
        return _.sortBy(ret, (deps: IDepartmentDetails[]) => {
            return deps.map(o => o.name.toLowerCase()).join('');
        })
    })

    private isLinkable(obj: IDepartmentDetails): boolean {
        if (this.isStandAlone) {
            return false;
        }
        if (global.permissionValidator.isMinSuperManager()) {
            return true;
        }
        const systemPermission = global.login.selectedOrganization()?.systempermission;
        return !!systemPermission?.find(p => this.getDepartmentsToTop(obj).map(o => o.id).includes(p.managerOf));
    }

    private isLeaf(obj: IDepartmentDetails, arr: IDepartmentDetails[]): boolean {
        return arr[arr.length - 1] == obj;
    }

    private onClick(obj: IDepartmentDetails, e: JQuery.ClickEvent) {
        if (this.params.onSelect) {
            this.params.onSelect(obj.data, e)
            return false
        }
        return true
    }

    private getSearchSplitButtonParams(): ISplitButtonParams {
        const hash = getHash('departmenthierarchy', {
            orgnr: this.__orgnr,
            id: this.__id,
        }, false) ?? '';
        return {
            mainButton: getRouteDropdownButton('departmenthierarchy', {
                cls: 'btn-action btn-sm',
                icon: 'fa-window',
                label: 'Open in new window',
                target: '_blank',
                href: () => `win.html#/${hash}`,
            })
        }
    }

    private shouldShowHeader(): boolean {
        return !!this.__routeParams?.request_?.startsWith('o/')
    }

    override dispose() {
        super.dispose();
        this.kosub.unsuscribeAll();
    }
}
